import React, { useState } from "react";
import { Button } from "reactstrap";
import { fmtNum } from "./trafficUtils";
import TrafficListDialog from "./TrafficListDialog";
import UrlCopyCell from "./UrlCopyCell";

const STAGE_BAR = {
	cloudflare: "monitoring__ai-traffic-chain-bar--cf",
	gsc: "monitoring__ai-traffic-chain-bar--gsc",
	prompts: "monitoring__ai-traffic-chain-bar--prompts",
	ga4: "monitoring__ai-traffic-chain-bar--ga4",
};

const SOURCE_TONE = {
	cloudflare: "monitoring__ai-traffic-chain-source--cf",
	gsc: "monitoring__ai-traffic-chain-source--gsc",
	prompts: "monitoring__ai-traffic-chain-source--prompts",
	ga4: "monitoring__ai-traffic-chain-source--ga4",
};

const SOURCE_LABEL = {
	cloudflare: "Cloudflare",
	gsc: "GSC",
	prompts: "Prompt runs",
	ga4: "GA4",
};

function stageUrls(stage) {
	const rows = stage?.urls ?? stage?.items ?? [];
	return Array.isArray(rows) ? rows.filter((r) => r?.url) : [];
}

function stageMetricColumn(stage) {
	const rows = stageUrls(stage);
	if (!rows.length) return null;
	if (rows.some((r) => r.visits != null)) {
		return {
			key: "visits",
			label: "Visits",
			align: "right",
			render: (r) => r.visits ?? "—",
		};
	}
	if (rows.some((r) => r.crawl_requests != null)) {
		return {
			key: "crawls",
			label: "Crawls",
			align: "right",
			render: (r) => r.crawl_requests ?? "—",
		};
	}
	if (rows.some((r) => r.clicks != null)) {
		return {
			key: "clicks",
			label: "Clicks",
			align: "right",
			render: (r) => r.clicks ?? "—",
		};
	}
	return null;
}

function Stage({ stage }) {
	const [open, setOpen] = useState(false);
	const { label, count, source, window, sample, note } = stage;
	const urls = stageUrls(stage);
	const metric = stageMetricColumn(stage);
	const columns = [
		{
			key: "url",
			label: "Page",
			render: (r) => <UrlCopyCell url={r.url} />,
		},
	];
	if (metric) columns.push(metric);

	return (
		<div className="monitoring__ai-traffic-chain-stage">
			<div
				className={`monitoring__ai-traffic-chain-bar ${
					STAGE_BAR[source] || "monitoring__ai-traffic-chain-bar--default"
				}`}
			/>
			<div className="monitoring__ai-traffic-chain-stage-label">{label}</div>
			<div className="monitoring__ai-traffic-chain-stage-count">
				{fmtNum(count)}
				{sample && (
					<span className="monitoring__ai-traffic-chain-sample">sample</span>
				)}
			</div>
			{urls.length > 0 && (
				<Button
					size="sm"
					color="primary"
					className="monitoring__action-btn monitoring__action-btn--primary monitoring__ai-traffic-chain-view-url"
					onClick={() => setOpen(true)}
				>
					View {urls.length === 1 ? "URL" : "URLs"}
				</Button>
			)}
			<div
				className={`monitoring__ai-traffic-chain-source ${
					SOURCE_TONE[source] || ""
				}`}
			>
				{SOURCE_LABEL[source] ?? source}
			</div>
			<div className="monitoring__ai-traffic-chain-window">{window}</div>
			{note && <div className="monitoring__ai-traffic-chain-note">{note}</div>}
			{open && (
				<TrafficListDialog
					title={label}
					detail={note || window}
					rows={urls}
					unit="URL"
					unitPlural="URLs"
					pageSize={10}
					rowKey={(r) => r.url}
					searchText={(r) => r.url}
					copyText={(r) => r.url}
					onClose={() => setOpen(false)}
					columns={columns}
				/>
			)}
		</div>
	);
}

export default function VisibilityChain({ data }) {
	const stages = data?.chain?.stages ?? [];
	return (
		<div className="monitoring__ai-traffic-kpi-card monitoring__ai-traffic-chain">
			<h3 className="monitoring__ai-traffic-section-title">Visibility chain</h3>
			<p className="monitoring__ai-traffic-section-note mb-3">
				Five stages from crawl to visit. <strong>Not a funnel</strong> — each stage
				states its own count, window and source. Pages that skip a stage appear in{" "}
				<strong>Signals</strong> below.
			</p>
			<div className="monitoring__ai-traffic-chain-stages">
				{stages.map((s) => (
					<Stage key={s.key} stage={s} />
				))}
			</div>
		</div>
	);
}
